/** Rasterize an SVG the way Scratch does (scratch-svg-renderer + font inlining). */

export async function rasterizeSvg(svg, scale = 2) {
  const str = String(svg || '');
  if (!str) return null;
  try {
    const mod = await import('scratch-svg-renderer');
    const SVGRenderer = mod.SVGRenderer || mod.default?.SVGRenderer || mod.default;
    if (typeof SVGRenderer !== 'function') throw new Error('no SVGRenderer');
    const r = new SVGRenderer();
    await new Promise((resolve, reject) => {
      try {
        r.loadSVG(str, false, () => resolve());
      } catch (e) {
        reject(e);
      }
    });
    r.draw(scale);
    return {
      canvas: r.canvas,
      width: r.size[0] || r.canvas.width / scale,
      height: r.size[1] || r.canvas.height / scale,
      x: r.viewOffset?.[0] || 0,
      y: r.viewOffset?.[1] || 0,
    };
  } catch {
    return await rasterizeSvgFallback(str);
  }
}

function rasterizeSvgFallback(svg) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({
      canvas: img,
      width: img.naturalWidth || 100,
      height: img.naturalHeight || 100,
      x: 0,
      y: 0,
    });
    img.onerror = () => resolve(null);
    img.src = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
  });
}

export function isEditorSvg(svg) {
  const s = String(svg || '');
  if (!/viewBox="0 0 480 360"/.test(s)) return false;
  if (/<image[\s>]/i.test(s)) return false;
  const paths = s.match(/<path[\s>]/gi);
  if (paths && paths.length > 24) return false;
  return true;
}

/** Width/height/viewBox of an SVG document, used to place imported costumes as live SVG. */
export function svgIntrinsicSize(svg) {
  const s = String(svg || '');
  const open = (s.match(/<svg\b[^>]*>/i) || [''])[0];
  const num = (attr) => {
    const m = open.match(new RegExp(`\\b${attr}\\s*=\\s*["']([^"']+)["']`, 'i'));
    if (!m) return 0;
    return Number(String(m[1]).replace(/px$/i, '')) || 0;
  };
  const w = num('width');
  const h = num('height');
  const vbRaw = (open.match(/\bviewBox\s*=\s*["']([^"']+)["']/i) || [])[1] || '';
  const vb = vbRaw.replace(/,/g, ' ').trim().split(/\s+/).map(Number);
  const vx = vb.length >= 4 && !Number.isNaN(vb[0]) ? vb[0] : 0;
  const vy = vb.length >= 4 && !Number.isNaN(vb[1]) ? vb[1] : 0;
  const vw = vb.length >= 4 && vb[2] > 0 ? vb[2] : w;
  const vh = vb.length >= 4 && vb[3] > 0 ? vb[3] : h;
  return {
    w: w || vw || 100,
    h: h || vh || 100,
    vx,
    vy,
    vw: vw || w || 100,
    vh: vh || h || 100,
  };
}

export function svgInnerMarkup(svg) {
  return String(svg || '')
    .replace(/<\?xml[^>]*>/i, '')
    .replace(/<!DOCTYPE[^>]*>/i, '')
    .replace(/^[\s\S]*?<svg\b[^>]*>/i, '')
    .replace(/<\/svg>\s*$/i, '');
}

const ART_W = 480;
const ART_H = 360;

/** Scale an imported SVG down so it fits the 480×360 costume artboard. */
export function fitSvgToArtboard(svg) {
  const size = svgIntrinsicSize(svg);
  const scale = Math.min(1, ART_W / (size.w || 1), ART_H / (size.h || 1));
  const w = size.w * scale;
  const h = size.h * scale;
  const x = (ART_W - w) / 2;
  const y = (ART_H - h) / 2;
  const inner = svgInnerMarkup(svg);
  const vb = `${size.vx} ${size.vy} ${size.vw} ${size.vh}`;
  return {
    svg: `<?xml version="1.0"?><svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${ART_W}" height="${ART_H}" viewBox="0 0 ${ART_W} ${ART_H}"><svg x="${x}" y="${y}" width="${w}" height="${h}" viewBox="${vb}" overflow="visible">${inner}</svg></svg>`,
    cx: ART_W / 2,
    cy: ART_H / 2,
  };
}

/** Draw an imported bitmap into a 2× PNG that fits the costume artboard. */
export function fitBitmapToArtboard(img) {
  const fit = Math.min(1, ART_W / (img.naturalWidth || img.width || 1), ART_H / (img.naturalHeight || img.height || 1));
  const dw = Math.max(1, Math.round((img.naturalWidth || img.width) * fit));
  const dh = Math.max(1, Math.round((img.naturalHeight || img.height) * fit));
  const cnv = document.createElement('canvas');
  cnv.width = dw * 2;
  cnv.height = dh * 2;
  const g = cnv.getContext('2d');
  g.imageSmoothingEnabled = true;
  g.imageSmoothingQuality = 'high';
  g.drawImage(img, 0, 0, cnv.width, cnv.height);
  return new Promise((resolve) => {
    cnv.toBlob((blob) => {
      if (!blob) {
        resolve({ data: new Uint8Array(), w: cnv.width, h: cnv.height });
        return;
      }
      blob.arrayBuffer().then((ab) => {
        resolve({ data: new Uint8Array(ab), w: cnv.width, h: cnv.height });
      });
    }, 'image/png');
  });
}
